import type { Asset } from './types';

/** Mirrors the API's limit on one tag. */
export const MAX_TAG_LENGTH = 40;

export type TagChanges = { add: string[]; remove: string[] };

/**
 * An asset's tags with `remove` taken off and `add` appended, the way the API
 * saves them: trimmed, repeats (ignoring case) dropped, existing spelling kept.
 */
export function applyTagChanges(tags: string[], { add, remove }: TagChanges) {
  const removed = new Set(remove.map((t) => t.toLowerCase()));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of [...tags.filter((t) => !removed.has(t.toLowerCase())), ...add]) {
    const tag = raw.trim().replace(/\s+/g, ' ');
    const folded = tag.toLowerCase();
    if (!tag || seen.has(folded)) continue;
    seen.add(folded);
    result.push(tag);
  }
  return result;
}

export type TagCount = {
  tag: string;
  /** How many of the selected assets have it. */
  onSelected: number;
  /** How many assets in the library have it. */
  total: number;
};

/**
 * Every tag in the library (one spelling each, ignoring case) with how many
 * selected assets have it: the selection's tags first, then the most used.
 */
export function tagCounts(
  assets: Pick<Asset, 'id' | 'tags'>[],
  selectedIds: string[],
): TagCount[] {
  const selected = new Set(selectedIds);
  const counts = new Map<string, TagCount>();
  for (const asset of assets) {
    for (const tag of asset.tags) {
      const key = tag.toLowerCase();
      const count = counts.get(key) ?? { tag, onSelected: 0, total: 0 };
      count.total += 1;
      if (selected.has(asset.id)) count.onSelected += 1;
      counts.set(key, count);
    }
  }
  return [...counts.values()].sort(
    (a, b) =>
      Number(b.onSelected > 0) - Number(a.onSelected > 0) ||
      b.total - a.total ||
      a.tag.localeCompare(b.tag),
  );
}
